import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { useCanvaStore } from "src/store/canva/canva.state";

interface ModalBoxProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  hideCloseButton?: boolean;
  className?: string;
  titleLeft?: boolean;
}

const ModalBox: React.FC<ModalBoxProps> = ({
  isOpen,
  onClose,
  title,
  children,
  hideCloseButton = false,
  className,
  titleLeft = false,
}) => {
  const { disableZoom, enableZoom } = useCanvaStore();
  const overlayRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      disableZoom();
      if (overlayRef.current && !overlayRef.current.open) {
        overlayRef.current.showModal?.();
      }
    } else {
      document.body.style.overflow = "";
      enableZoom();
      overlayRef.current?.close();
    }
    return () => {
      document.body.style.overflow = "";
      enableZoom();
    };
  }, [isOpen, disableZoom, enableZoom]);

  const closeModal = () => {
    onClose();
    enableZoom();
    overlayRef.current?.close();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === overlayRef.current) {
      closeModal();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
      closeModal();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <dialog
      ref={overlayRef}
      className={styles.modalOverlay}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className={classNames(styles.modalContent, className)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!hideCloseButton && (
          <button
            type="button"
            className={styles.modalCloseButton}
            onClick={closeModal}
          >
            <span className={styles.closeIcon} />
          </button>
        )}
        <h2
          id="modal-title"
          className={titleLeft ? styles.modalTitleLeft : styles.modalTitle}
        >
          {title}
        </h2>
        {children}
      </div>
    </dialog>,
    document.body,
  );
};

export default ModalBox;
